// js/utils/ShopifySectionsAPI.js
/**
 * Utility class for fetching product data using Shopify Sections API
 * Shared between wishlist-section.js and recently-viewed-products.js
 */

export class ShopifySectionsAPI {
  constructor() {
    this.productCache = new Map();
  }

  /**
   * Fetch products using Shopify Search API (for localStorage-based product lists)
   * @param {Array} productHandles - Array of product handles
   * @param {string} sectionId - Section ID for the search
   * @param {Object} options - Additional options
   * @returns {Promise<Array>} Array of product objects with HTML
   */
  async fetchProducts(productHandles, sectionId = 'main-search', options = {}) {
    if (!productHandles || productHandles.length === 0) {
      return [];
    }

    // Detect if we have product IDs (numeric) or handles (string)
    const isNumeric = productHandles.every(item => /^\d+$/.test(item));
    
    const cacheKey = `search-${sectionId}-${isNumeric ? 'ids' : 'handles'}-${productHandles.join(',')}`;
    
    // Check cache first
    if (this.productCache.has(cacheKey)) {
      return this.productCache.get(cacheKey);
    }

    try {
      const queryString = productHandles.map(item => 
        isNumeric ? `id:${item}` : `handle:${item}`
      ).join(' OR ');
      
      // Encode the query string properly for URL
      const encodedQuery = encodeURIComponent(queryString);
      
      const searchUrl = `${window.routes.searchUrl}?section_id=${sectionId}&type=product&q=${encodedQuery}`;
      
      const response = await fetch(searchUrl, {
        credentials: "same-origin",
        method: "GET"
      });

      if (!response.ok) {
        throw new Error(`Search request failed: ${response.status}`);
      }

      const content = await response.text();
      
      // Extract the relevant content from the search results
      const tempElement = document.createElement("div");
      tempElement.innerHTML = content;
      
      // Find the section content
      const sectionContent = tempElement.querySelector(`[data-section-id="${sectionId}"]`);
      
      if (sectionContent) {
        // Extract product items from the search results
        const productItems = sectionContent.querySelectorAll('.product-item');
        
        const products = Array.from(productItems).map((item, index) => {
          // Try to extract handle from the product item if we have IDs
          let handle = productHandles[index] || 'unknown';
          if (isNumeric) {
            // For product IDs, try to extract handle from data attributes
            const handleAttr = item.querySelector('[data-product-handle]');
            if (handleAttr) {
              handle = handleAttr.getAttribute('data-product-handle');
            }
          }
          
          return {
            handle: handle,
            element: item,
            html: item.outerHTML
          };
        });
        
        // Cache the results
        this.productCache.set(cacheKey, products);
        return products;
      }
      
      // Fallback: return empty array if no section content found
      this.productCache.set(cacheKey, []);
      return [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Fetch products using Shopify Search API (for recently viewed products)
   * @param {Array} productIds - Array of product IDs
   * @param {string} sectionId - Section ID for the search
   * @returns {Promise<string>} HTML content from search results
   */
  async fetchProductsBySearch(productIds, sectionId) {
    if (!productIds || productIds.length === 0) {
      return '';
    }

    const queryString = productIds.map(id => `id:${id}`).join(' OR ');
    const searchUrl = `${window.routes.searchUrl}?section_id=${sectionId}&type=product&q=${queryString}`;
    
    // Check cache first
    const cacheKey = `search-${sectionId}-${productIds.join(',')}`;
    if (this.productCache.has(cacheKey)) {
      return this.productCache.get(cacheKey);
    }
    
    try {
      const response = await fetch(searchUrl, {
        credentials: "same-origin",
        method: "GET"
      });

      if (!response.ok) {
        throw new Error(`Search request failed: ${response.status}`);
      }

      const content = await response.text();
      
      // Extract the relevant content from the search results
      const tempElement = document.createElement("div");
      tempElement.innerHTML = content;
      
      // Find the section content (this will depend on your search template structure)
      const sectionContent = tempElement.querySelector(`[data-section-id="${sectionId}"]`);
      if (sectionContent) {
        const result = sectionContent.innerHTML;
        // Cache the result
        this.productCache.set(cacheKey, result);
        return result;
      }
      
      // Cache the full content as fallback
      this.productCache.set(cacheKey, content);
      return content;
    } catch (error) {
      return '';
    }
  }

  /**
   * Render products HTML into a container
   * @param {Array} products - Array of product objects
   * @param {HTMLElement} container - Container element
   * @param {Object} options - Rendering options
   */
  renderProducts(products, container, options = {}) {
    if (products.length === 0) {
      container.innerHTML = options.emptyMessage || '<p>No products found.</p>';
      return;
    }
    
    // Use the actual HTML from Shopify Sections API
    const html = products.map(product => product.html).join('');
    
    container.innerHTML = html;

    // Note: Custom elements reinitialization is handled by the calling component
    // (e.g., recently-viewed-products creates its own slider)
  }


  /**
   * Clear the product cache
   */
  clearCache() {
    this.productCache.clear();
  }

  /**
   * Get cache size
   * @returns {number} Number of cached items
   */
  getCacheSize() {
    return this.productCache.size;
  }

  /**
   * Invalidate cache for specific products
   * @param {Array} handles - Product handles to invalidate
   */
  invalidateCache(handles) {
    handles.forEach(handle => {
      // Remove all cache entries that contain this handle
      for (const [key, value] of this.productCache.entries()) {
        if (key.includes(handle)) {
          this.productCache.delete(key);
        }
      }
    });
  }

  /**
   * Invalidate cache for specific product IDs (for search results)
   * @param {Array} productIds - Product IDs to invalidate
   */
  invalidateSearchCache(productIds) {
    productIds.forEach(id => {
      // Remove all cache entries that contain this product ID
      for (const [key, value] of this.productCache.entries()) {
        if (key.includes(`search-`) && key.includes(id)) {
          this.productCache.delete(key);
        }
      }
    });
  }

  /**
   * Get cache health statistics
   * @returns {Object} Cache health information
   */
  getCacheHealth() {
    const total = this.productCache.size;
    const keys = Array.from(this.productCache.keys());
    
    return {
      total,
      keys,
      memoryUsage: JSON.stringify(keys).length, // Rough estimate
      productCache: keys.filter(key => !key.includes('search-')).length,
      searchCache: keys.filter(key => key.includes('search-')).length
    };
  }

  /**
   * Debug method to log cache contents
   */
  debugCache() {
    return {
      size: this.productCache.size,
      keys: Array.from(this.productCache.keys()),
      health: this.getCacheHealth()
    };
  }

  /**
   * Get all cached product handles
   * @returns {Array} Array of cached product handles
   */
  getCachedHandles() {
    return Array.from(this.productCache.keys())
      .filter(key => !key.includes('search-'))
      .map(key => key.split('-').slice(1).join('-'));
  }

  /**
   * Get all cached search results
   * @returns {Array} Array of cached search keys
   */
  getCachedSearchResults() {
    return Array.from(this.productCache.keys())
      .filter(key => key.includes('search-'));
  }

  /**
   * Check if a product is cached
   * @param {string} handle - Product handle
   * @param {string} view - View type
   * @returns {boolean} True if product is cached
   */
  isProductCached(handle, view = 'product-item') {
    const cacheKey = `${view}-${handle}`;
    return this.productCache.has(cacheKey);
  }

  /**
   * Check if search results are cached
   * @param {Array} productIds - Product IDs
   * @param {string} sectionId - Section ID
   * @returns {boolean} True if search results are cached
   */
  isSearchCached(productIds, sectionId) {
    const cacheKey = `search-${sectionId}-${productIds.join(',')}`;
    return this.productCache.has(cacheKey);
  }
}

// Export singleton instance
export const shopifySectionsAPI = new ShopifySectionsAPI();
